import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Row,
  Button,
  Table,
  Badge,
  Spinner,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from 'reactstrap';
import { toast } from 'react-toastify';
import PaginationComponent from '../../Components/Common/Pagination';
import {
  requestPresignedUrl,
  uploadFileToS3,
  fetchCallLogSummary,
  fetchUploadedCallLogs,
  deleteCallLog,
  downloadCallLog,
} from '../../slices/callLog/thunk';
import { resetUpload, clearError } from '../../slices/callLog/reducer';

const CallTab = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(5);
  const dispatch = useDispatch();
  const fileInputRef = useRef(null);

  // Redux state using useSelector
  const {
    uploadedCallLogs,
    totalCount,
    currentCallLog,
    callLogSummary,
    preSignedData,
    loading,
    listLoading,
    summaryLoading,
    deleteLoading,
    error
  } = useSelector((state) => state.CallLog);

  // Local state
  const [selectedFile, setSelectedFile] = useState(null);
  const [deleteModal, setDeleteModal] = useState(false);
  const [callLogToDelete, setCallLogToDelete] = useState(null);
  const [viewModal, setViewModal] = useState(false);
  const [selectedCallLog, setSelectedCallLog] = useState(null);
  const [audioPreview, setAudioPreview] = useState({ callLogId: null, url: null });
  const [audioLoadingId, setAudioLoadingId] = useState(null);
  const audioPlayerRef = useRef(null);

  const previewedCallLog = useMemo(() => {
    if (!audioPreview?.callLogId || !uploadedCallLogs?.length) {
      return null;
    }
    return uploadedCallLogs.find(
      (log) => log.recording_id === audioPreview.callLogId
    ) || null;
  }, [audioPreview?.callLogId, uploadedCallLogs]);

  const transcriptText = useMemo(() => {
    if (!callLogSummary) {
      return '';
    }

    if (typeof callLogSummary.transcription === 'string') {
      return callLogSummary.transcription;
    }

    if (Array.isArray(callLogSummary.transcription)) {
      return callLogSummary.transcription.join('\n');
    }

    if (typeof callLogSummary?.metadata?.transcription === 'string') {
      return callLogSummary.metadata.transcription;
    }

    return '';
  }, [callLogSummary]);

  const summaryText = useMemo(() => {
    if (!callLogSummary) {
      return '';
    }

    if (typeof callLogSummary.summary === 'string') {
      return callLogSummary.summary;
    }

    if (Array.isArray(callLogSummary.summary)) {
      return callLogSummary.summary.join('\n');
    }

    return '';
  }, [callLogSummary]);

  // Set document title
  document.title = "Whistlemind Demo Engine | Call Log";

  // Clear any stale errors when component mounts
  useEffect(() => {
    dispatch(clearError());
  }, [dispatch]);

  // Fetch uploaded call logs on component mount
  useEffect(() => {
    const offset = (currentPage - 1) * itemsPerPage;
    dispatch(fetchUploadedCallLogs({ itemsPerPage, offset }));
  }, [dispatch, currentPage]);

  // Display error alerts (only show if not loading to avoid showing stale errors)
  useEffect(() => {
    if (error && !listLoading) {
      toast.error(error);
    }
  }, [error, listLoading]);

  // Cleanup audio preview URL when component unmounts or URL changes
  useEffect(() => {
    return () => {
      if (audioPreview?.url && audioPreview.url.startsWith('blob:')) {
        window.URL.revokeObjectURL(audioPreview.url);
      }
    };
  }, [audioPreview?.url]);

  useEffect(() => {
    if (audioPreview?.url && audioPlayerRef?.current) {
      try {
        audioPlayerRef.current.load();
        const playPromise = audioPlayerRef.current.play();
        if (playPromise !== undefined) {
          playPromise.catch(() => {
            // Autoplay might be blocked; no action needed
          });
        }
      } catch (err) {
        // Ignore playback errors (e.g., user gesture requirement)
      }
    }
  }, [audioPreview?.url]);

  // Toggle delete modal
  const toggleDeleteModal = () => {
    setDeleteModal(!deleteModal);
    if (deleteModal) {
      setCallLogToDelete(null);
    }
  };

  // Toggle view modal
  const toggleViewModal = () => {
    setViewModal(!viewModal);
    if (viewModal) {
      setSelectedCallLog(null);
    }
  };

  // Handle delete click
  const handleDeleteClick = (callLog) => {
    setCallLogToDelete(callLog);
    setDeleteModal(true);
  };

  // Confirm delete
  const confirmDelete = async () => {
    if (callLogToDelete) {
      try {
        await dispatch(deleteCallLog({ callLogId: callLogToDelete.recording_id })).unwrap();
        toast.success(`Call log "${callLogToDelete.file_name || callLogToDelete.name}" deleted successfully`);

        // Check if this was the last item on the current page
        const remainingItemsOnPage = uploadedCallLogs.length - 1;

        // If no items remain on current page and we're not on page 1, go to previous page
        if (remainingItemsOnPage === 0 && currentPage > 1) {
          const newPage = currentPage - 1;
          setCurrentPage(newPage);
          const offset = (newPage - 1) * itemsPerPage;
          dispatch(fetchUploadedCallLogs({ itemsPerPage, offset }));
        } else {
          // Otherwise, just refresh the current page
          const offset = (currentPage - 1) * itemsPerPage;
          dispatch(fetchUploadedCallLogs({ itemsPerPage, offset }));
        }

        toggleDeleteModal();
      } catch (error) {
        toast.error('Failed to delete call log. Please try again.');
      }
    }
  };

  // Handle upload button click
  const handleUploadClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // Handle file selection and upload
  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type - check by extension since audio MIME types can vary
    const allowedExtensions = ['.mp3', '.wav', '.m4a', '.ogg', '.aac', '.flac'];
    const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
    const allowedMimeTypes = ['audio/mpeg', 'audio/mp3', 'audio/wav', 'audio/x-wav', 'audio/m4a', 'audio/x-m4a', 'audio/ogg', 'audio/aac'];

    if (!allowedExtensions.includes(fileExtension) && !allowedMimeTypes.includes(file.type)) {
      toast.warning('Please select an audio file (MP3, WAV, M4A, OGG, AAC, FLAC)');
      event.target.value = '';
      return;
    }

    // Validate file size (e.g., max 50MB for audio files)
    const maxSize = 50 * 1024 * 1024; // 50MB
    if (file.size > maxSize) {
      toast.warning('File size must be less than 50MB');
      event.target.value = '';
      return;
    }

    setSelectedFile(file);
    dispatch(resetUpload());

    try {
      // Step 1: Request pre-signed URL
      toast.info(`Uploading "${file.name}"...`);

      const urlResult = await dispatch(
        requestPresignedUrl({
          fileName: file.name,
          fileType: file.type,
          fileSize: file.size
        })
      ).unwrap();

      // Step 2: Upload to S3
      await dispatch(
        uploadFileToS3({
          file,
          preSignedUrl: urlResult.preSignedUrl
        })
      ).unwrap();

      toast.success('Call recording uploaded successfully! Processing...');

      // Refresh call log list
      const offset = (currentPage - 1) * itemsPerPage;
      dispatch(fetchUploadedCallLogs({ itemsPerPage, offset }));

    } catch (error) {
      const errorMessage = error || 'Upload failed. Please try again.';
      toast.error(errorMessage);
    }

    // Reset file input
    event.target.value = '';
  };

  // Handle call log download
  const handleDownloadCallLog = async (callLogId, fileName) => {
    try {
      toast.info('Preparing download...');

      await dispatch(downloadCallLog({ callLogId, fileName })).unwrap();

      toast.success('Call log downloaded successfully');
    } catch (error) {
      toast.error('Failed to download call log');
    }
  };

  // Handle preview/play call log - Updated to use s3_recording_url directly
  const handlePlayCallLog = async (callLog) => {
    if (!callLog) {
      toast.warning('Unable to preview this call log');
      return;
    }

    // If the same audio is already playing, restart it
    if (audioPreview.callLogId === callLog.recording_id && audioPreview.url) {
      try {
        if (audioPlayerRef.current) {
          audioPlayerRef.current.currentTime = 0;
          const playPromise = audioPlayerRef.current.play();
          if (playPromise !== undefined) {
            playPromise.catch(() => { });
          }
        }
      } catch (err) {
        // Ignore playback errors
      }
      return;
    }

    // Check if s3_recording_url is available
    if (!callLog.s3_recording_url) {
      toast.error('Recording URL is not available for this call log');
      return;
    }

    setAudioLoadingId(callLog.recording_id);

    try {
      // Clean up previous URL if it was a blob
      setAudioPreview((prev) => {
        if (prev?.url && prev.url.startsWith('blob:')) {
          window.URL.revokeObjectURL(prev.url);
        }
        return { callLogId: callLog.recording_id, url: callLog.s3_recording_url };
      });

      toast.success('Audio loaded successfully');
    } catch (error) {
      toast.error('Failed to load audio preview');
      setAudioPreview({ callLogId: null, url: null });
    } finally {
      setAudioLoadingId(null);
    }
  };

  // Handle view call log (fetch summary and open modal)
  const handleViewCallLog = async (callLog) => {
    if (callLog.recording_id) {
      setSelectedCallLog(callLog);
      setViewModal(true);

      try {
        await dispatch(
          fetchCallLogSummary({ correlationId: callLog.recording_id })
        ).unwrap();
      } catch (error) {
        toast.error('Failed to fetch call log summary');
      }
    } else {
      toast.warning('No correlation ID found for this call log');
    }
  };

  // Copy transcription to clipboard
  const handleCopyTranscript = () => {
    if (transcriptText) {
      navigator.clipboard.writeText(transcriptText);
      toast.success('Transcript copied to clipboard!');
    }
  };

  const handleExportTranscript = () => {
    if (transcriptText) {
      const blob = new Blob([transcriptText], { type: 'text/plain' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'call-log-transcript.txt');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success('Transcript exported successfully!');
    }
  };

  const handleCopySummary = () => {
    if (summaryText) {
      navigator.clipboard.writeText(summaryText);
      toast.success('Summary copied to clipboard!');
    }
  };

  // Export summary as text file
  const handleExportSummary = () => {
    if (summaryText) {
      const blob = new Blob([summaryText], { type: 'text/plain' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'call-log-summary.txt');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success('Summary exported successfully!');
    }
  };

  // Get status badge
  const getStatusBadge = (status) => {
    const colors = {
      pending_upload: 'warning',
      processing: 'info',
      transcribing: 'primary',
      completed: 'success',
      failed: 'danger',
    };
    return <Badge color={colors[status] || 'secondary'}>{getStatusMessage(status)}</Badge>;
  };

  const getStatusMessage = (status) => {
    switch (status) {
      case 'pending_upload':
        return 'Pending';
      case 'completed':
        return 'Completed';
      case 'processing':
        return 'Processing';
      case "transcribing":
        return 'Transcribing';
      case "failed":
        return 'Failed';
      default:
        return 'Unknown status';
    }
  };

  // Handle page change
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    const offset = (pageNumber - 1) * itemsPerPage;
    dispatch(fetchUploadedCallLogs({ itemsPerPage, offset }));
  };

  const totalPages = Math.ceil(totalCount / itemsPerPage);

  return (
    <React.Fragment>
      <div>
        <Container fluid>
          {/* Main Content Area */}
          <Row>
            <Col>
              {/* Upload Button */}
              <div className="d-flex gap-2 mb-3">
                <Button
                  color="primary"
                  size="sm"
                  onClick={handleUploadClick}
                  disabled={loading}
                >
                  
                  {loading ? <Spinner size="sm" className='me-2' /> : <><i className="ri-upload-line me-2"></i>Upload Call Recording</>}
                </Button>

                {/* Refresh Button */}
                <Button
                  color="secondary"
                  size="sm"
                  onClick={() => {
                    const offset = (currentPage - 1) * itemsPerPage;
                    dispatch(fetchUploadedCallLogs({ itemsPerPage, offset }));
                    // toast.info('Refreshing file list...');
                  }}
                  disabled={listLoading || loading}
                >

                  <>
                    <i className="ri-refresh-line me-2"></i> Refresh
                  </>

                </Button>

                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  accept="audio/*,.mp3,.wav,.m4a,.ogg"
                  style={{ display: 'none' }}
                />
              </div>
            </Col>
          </Row>

          <Row>
            {/* List Uploaded Files - Full Width */}
            <Col xl={12}>
              {/* List Uploaded Files Card */}
              <Card>
                <CardHeader>
                  <h5 className="card-title mb-0">List Uploaded Call Logs</h5>
                </CardHeader>
                <CardBody>
                  {/* Show loading spinner when fetching data */}
                  {listLoading ? (
                    <div className="text-center py-5">
                      <Spinner color="primary" />
                      <p className="mt-2 text-muted">Loading call logs...</p>
                    </div>
                  ) : uploadedCallLogs && uploadedCallLogs.length > 0 ? (
                    <>
                      {/* Table */}
                      <div className="table-responsive">
                        <Table className="table-nowrap mb-0">
                          <thead className="table-light">
                            <tr>
                              <th>File Name</th>
                              <th>Status</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {uploadedCallLogs.map((callLog) => (
                              <tr key={callLog.recording_id}>
                                <td>
                                  <div>
                                    <i className="ri-mic-line text-primary me-2"></i>
                                    <span className="fw-medium">{callLog.file_name || callLog.name}</span>
                                  </div>
                                  <small className="text-muted">
                                    {callLog.updated_at || callLog.upload_date}
                                  </small>
                                </td>
                                <td>{getStatusBadge(callLog.status || 'Uploaded')}</td>
                                <td>
                                  <div className="d-flex gap-2">
                                    <UncontrolledDropdown>
                                      <DropdownToggle
                                        href="#"
                                        className="btn btn-soft-secondary btn-sm dropdown"
                                        tag="button"
                                      >
                                        <i className="ri-more-fill align-middle"></i>
                                      </DropdownToggle>
                                      <DropdownMenu
                                        className="dropdown-menu-end"
                                        container="body"
                                        style={{ zIndex: 1050 }}
                                      >
                                        <DropdownItem
                                          onClick={() => handleViewCallLog(callLog)}
                                          disabled={callLog.status === 'processing' || callLog.status === 'pending_upload' || callLog.status === 'failed' || callLog.status === "transcribing"}
                                        >
                                          <i className="ri-eye-line me-2"></i>
                                          View Summary
                                        </DropdownItem>
                                        {/* <DropdownItem
                                          onClick={() => handleDownloadCallLog(callLog.recording_id, callLog.file_name)}
                                          disabled={callLog.status === 'processing' || callLog.status === 'pending_upload'}
                                        >
                                          <i className="ri-download-line me-2"></i>
                                          Download
                                        </DropdownItem> */}
                                        <DropdownItem divider />
                                        <DropdownItem
                                          onClick={() => handleDeleteClick(callLog)}
                                          className="text-danger"
                                        >
                                          <i className="ri-delete-bin-line me-2"></i>
                                          Delete
                                        </DropdownItem>
                                      </DropdownMenu>
                                    </UncontrolledDropdown>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>

                      {audioPreview.url && (
                        <div className="mt-3">
                          <div className="p-3 border rounded bg-light">
                            <div className="d-flex flex-column flex-lg-row align-items-start align-items-lg-center gap-3">
                              <div>
                                <h6 className="mb-1">Now Playing</h6>
                                <p className="text-muted mb-0">
                                  {previewedCallLog?.file_name || previewedCallLog?.name || 'Selected call log'}
                                </p>
                              </div>
                              <audio
                                ref={audioPlayerRef}
                                controls
                                autoPlay
                                src={audioPreview.url}
                                style={{ width: '100%' }}
                              >
                                Your browser does not support the audio element.
                              </audio>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Pagination Info */}
                      <Row className="mt-3">
                        <Col>
                          <p className="text-muted mb-0">
                            Showing {(currentPage - 1) * itemsPerPage + 1} to{' '}
                            {Math.min(currentPage * itemsPerPage, totalCount)} of{' '}
                            {totalCount} entries
                          </p>
                        </Col>
                      </Row>

                      {/* Pagination - Only show if there are multiple pages */}
                      {totalPages > 1 && (
                        <PaginationComponent
                          currentPage={currentPage}
                          totalPages={totalPages}
                          handlePageChange={handlePageChange}
                        />
                      )}
                    </>
                  ) : (
                    /* Empty State */
                    <div className="text-center py-5">
                      <i
                        className="ri-mic-line text-muted"
                        style={{ fontSize: '3rem' }}
                      ></i>
                      <p className="text-muted mt-3 mb-1">No call recordings uploaded yet</p>
                      <p className="text-muted small">
                        Upload an audio file to see it listed here
                      </p>
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      {/* View Call Log Modal */}
      <Modal
        isOpen={viewModal}
        toggle={toggleViewModal}
        style={{ maxWidth: '90%', width: '90%' }}
        centered
        backdrop="static"
      >
        <ModalHeader toggle={toggleViewModal}>
          <div className="d-flex align-items-center gap-2">
            <i className="ri-mic-line"></i>
            Call Log Details - {selectedCallLog?.file_name || selectedCallLog?.name || 'N/A'}
          </div>
        </ModalHeader>
        <ModalBody style={{ minHeight: '500px', maxHeight: '80vh', overflowY: 'auto' }}>
          {summaryLoading ? (
            <div className="text-center py-5">
              <Spinner color="primary" style={{ width: '3rem', height: '3rem' }} />
              <p className="text-muted mt-3">Loading call log data...</p>
            </div>
          ) : (
            <>
              {callLogSummary?.s3_recording_url && (
                <div className="mb-4">
                  <div className="p-3 border rounded bg-light">
                    <div className="d-flex align-items-center gap-3">
                      <i className="ri-mic-line text-primary fs-4"></i>
                      <div className="flex-grow-1">
                        <h6 className="mb-1">Audio Recording</h6>
                        <audio
                          controls
                          src={callLogSummary.s3_recording_url}
                          style={{ width: '100%' }}
                          controlsList="nodownload"
                        >
                          Your browser does not support the audio element.
                        </audio>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              <Row className="g-0">
                <Col md={6} className="pe-3 border-end">
                  <div className="d-flex flex-column h-100">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h6 className="mb-0 text-muted">Transcript</h6>
                      <Button
                        color="primary"
                        size="sm"
                        onClick={handleCopyTranscript}
                        disabled={!transcriptText}
                      >
                        <i className="ri-file-copy-line me-1"></i>
                        Copy
                      </Button>
                    </div>
                    <div
                      className="flex-grow-1 rounded p-3 bg-light"
                      style={{
                        minHeight: '500px',
                        maxHeight: '500px',
                        overflowY: 'auto'
                      }}
                    >
                      {transcriptText ? (
                        <pre
                          className="mb-0"
                          style={{
                            whiteSpace: 'pre-wrap',
                            fontFamily: 'inherit',
                            fontSize: '0.9rem',
                            lineHeight: '1.6'
                          }}
                        >
                          {transcriptText}
                        </pre>
                      ) : (
                        <div className="text-center py-5">
                          <i className="ri-file-text-line text-muted" style={{ fontSize: '3rem' }}></i>
                          <p className="text-muted mt-3">No transcript available</p>
                        </div>
                      )}
                    </div>
                    {transcriptText && (
                      <div className="mt-3">
                        <Button
                          color="outline-secondary"
                          size="sm"
                          onClick={handleExportTranscript}
                        >
                          <i className="ri-download-line me-1"></i>
                          Export Transcript
                        </Button>
                      </div>
                    )}
                  </div>
                </Col>

                <Col md={6} className="ps-3">
                  <div className="d-flex flex-column h-100">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h6 className="mb-0 text-muted">AI Call Summary</h6>
                      <Button
                        color="primary"
                        size="sm"
                        onClick={handleCopySummary}
                        disabled={!summaryText}
                      >
                        <i className="ri-file-copy-line me-1"></i>
                        Copy
                      </Button>
                    </div>
                    <div
                      className="flex-grow-1 rounded p-3 bg-light"
                      style={{
                        minHeight: '500px',
                        maxHeight: '500px',
                        overflowY: 'auto'
                      }}
                    >
                      {summaryText ? (
                        <pre
                          className="mb-0"
                          style={{
                            whiteSpace: 'pre-wrap',
                            fontFamily: 'inherit',
                            fontSize: '0.9rem',
                            lineHeight: '1.6'
                          }}
                        >
                          {summaryText}
                        </pre>
                      ) : (
                        <div className="text-center py-5">
                          <i className="ri-robot-line text-muted" style={{ fontSize: '3rem' }}></i>
                          <p className="text-muted mt-3">No summary available</p>
                        </div>
                      )}
                    </div>

                    {callLogSummary?.metadata && (
                      <div className="mt-3 p-2 bg-secondary bg-opacity-10 rounded">
                        <small className="text-muted">
                          <strong>Metadata:</strong> {JSON.stringify(callLogSummary.metadata)}
                        </small>
                      </div>
                    )}

                    {summaryText && (
                      <div className="mt-3">
                        <Button
                          color="outline-secondary"
                          size="sm"
                          onClick={handleExportSummary}
                        >
                          <i className="ri-download-line me-1"></i>
                          Export Summary
                        </Button>
                      </div>
                    )}
                  </div>
                </Col>
              </Row>
            </>

          )}
        </ModalBody>
        <ModalFooter>
        </ModalFooter>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={deleteModal} toggle={toggleDeleteModal} centered backdrop="static">
        <ModalHeader toggle={toggleDeleteModal}>
          Confirm Delete
        </ModalHeader>
        <ModalBody>
          <div className="text-center">
            <i className="ri-delete-bin-line text-danger" style={{ fontSize: '3.5rem' }}></i>
            <h5 className="mt-3 mb-2">Are you sure?</h5>
            <p className="text-muted mb-1">
              Do you want to delete this call log?
            </p>
            {callLogToDelete && (
              <div className="mt-3 p-3 bg-light rounded">
                <div className="d-flex align-items-center justify-content-center">
                  <i className="ri-mic-line text-primary me-2"></i>
                  <strong>{callLogToDelete.file_name || callLogToDelete.name}</strong>
                </div>
              </div>
            )}
            <p className="text-muted mt-3 mb-0">
              <small>This action cannot be undone.</small>
            </p>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={toggleDeleteModal} disabled={deleteLoading}>
            <i className="ri-close-line me-1"></i>
            Cancel
          </Button>
          <Button color="danger" onClick={confirmDelete} disabled={deleteLoading}>
            <i className="ri-delete-bin-line me-1"></i>
            {deleteLoading ? "Deleting..." : "Delete"}
          </Button>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
};

export default CallTab;
